import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import { adminSurfaceSx, adminPageSx } from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import { adminRequest } from "../../utils/adminApi";

// Admin UI for worker POST /api/admin/reset-account (worker #468, contract v58).
// Resets a NAMED user's account to a known state so a flow can be re-tested end to
// end — the targeted sibling of reset-onboarding, which can only ever reset the
// caller. Fires through the resetAccount proxy, which forwards to the worker
// (admin-gated server-side).
//
// This is the most destructive control in the console: `full` deletes every workout
// INCLUDING templates, the whole plan tree, and drops premium entitlement. So unlike
// Purge Workouts — which confirms with a restatement — the confirm here is ARMED by
// typing the target email exactly. A restatement you can click past is not a
// meaningful guard when the blast radius is someone's entire training history.

// What each scope destroys, stated in the UI rather than left to the API docs, so
// the operator picks a scope while looking at its consequences.
const SCOPES = {
  learned: {
    label: "Learned",
    chip: "reversible-ish",
    summary: "Cold start for the coach. Training data survives.",
    wipes: [
      "Profile / onboarding intake",
      "Coach long-term memory",
      "Chat history + conversations",
      "Chat-suggestions cache (derived from the above)",
    ],
    keeps: ["Login", "All workouts + templates", "Plans", "Premium entitlement"],
  },
  full: {
    label: "Full",
    chip: "irreversible",
    summary: "Everything above, plus all training data and entitlement.",
    wipes: [
      "Everything in Learned, plus:",
      "Every workout INCLUDING templates (full delete cascade)",
      "The entire plan tree + planned sessions",
      "Drafts, prescription overrides, reflection state, set logs",
      "AI usage counters",
      "AccountSubscription — premium is DROPPED",
    ],
    keeps: [
      "Login (Users row)",
      "Apple transaction map — restore-purchase re-grants premium legitimately",
    ],
  },
};

export default function ResetAccount() {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token; staging uses the held staging token
  // and routes via ?env=staging (adminRequest).
  const { env, token } = useSelector(adminRequest);
  const [targetEmail, setTargetEmail] = useState("");
  const [scope, setScope] = useState("learned");
  const [emailError, setEmailError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The arming input inside the confirm dialog — must match targetEmail exactly.
  const [confirmEmail, setConfirmEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  const [snack, setSnack] = useState("");
  const [result, setResult] = useState(null);

  const trimmedTarget = targetEmail.trim();
  // Case-insensitive because email is case-insensitive in practice, but otherwise
  // exact — no trimming slack beyond the outer whitespace the operator can't see.
  const armed = confirmEmail.trim().toLowerCase() === trimmedTarget.toLowerCase() && trimmedTarget !== "";

  const openConfirm = () => {
    if (!trimmedTarget) {
      setEmailError("Target user email is required");
      return;
    }
    setEmailError("");
    setConfirmEmail("");
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmEmail("");
  };

  const handleConfirm = async () => {
    if (!armed) return; // belt-and-braces; the button is disabled too.
    setConfirmOpen(false);
    setConfirmEmail("");
    if (env === "staging" && !token) {
      setSnack("Log in to staging first.");
      return;
    }
    setResetting(true);
    setResult(null);
    try {
      // env is routing-only; the proxy strips it before forwarding.
      const envQuery = env === "staging" ? "?env=staging" : "";
      const response = await axios.post(
        `/api/admin/resetAccount${envQuery}`,
        { target_email: trimmedTarget, scope },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data || {};
      setResult(data);
      const cleared = data.tables_cleared?.length ?? 0;
      setSnack(
        `Reset ${trimmedTarget} (${data.scope ?? scope}) — ${cleared} table${cleared === 1 ? "" : "s"} cleared, ${data.workouts_deleted ?? 0} workout${data.workouts_deleted === 1 ? "" : "s"} deleted.`
      );
    } catch (err) {
      console.error("Failed to reset account:", err);
      const message =
        err.response?.data?.error || err.message || "Failed to reset account.";
      setSnack(message);
    } finally {
      setResetting(false);
    }
  };

  const active = SCOPES[scope];

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<RestartAltIcon />}
          iconAccent={theme.palette.error.main}
          title="Reset account"
          subtitle="Wipe a named user's account back to a known state so a flow can be re-tested (worker #468). Destructive — the confirm is armed by typing the target email."
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <TextField
                  fullWidth
                  label="Target user email"
                  type="email"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  error={!!emailError}
                  helperText={
                    emailError ||
                    "The account to reset — not necessarily your own."
                  }
                  margin="normal"
                  slotProps={{ htmlInput: { "data-testid": "reset-email" } }}
                />

                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, fontWeight: 700 }}
                >
                  Scope
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={scope}
                  onChange={(_, v) => v && setScope(v)}
                  size="small"
                  data-testid="reset-scope"
                >
                  <ToggleButton value="learned" data-testid="reset-scope-learned">
                    Learned
                  </ToggleButton>
                  <ToggleButton value="full" data-testid="reset-scope-full">
                    Full
                  </ToggleButton>
                </ToggleButtonGroup>

                <Alert
                  severity={scope === "full" ? "error" : "info"}
                  icon={scope === "full" ? <WarningAmberIcon /> : undefined}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {active.summary}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 1, fontWeight: 700 }}>
                    Wipes
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {active.wipes.map((w) => (
                      <li key={w}>
                        <Typography variant="caption">{w}</Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="caption" component="div" sx={{ mt: 1, fontWeight: 700 }}>
                    Keeps
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {active.keeps.map((k) => (
                      <li key={k}>
                        <Typography variant="caption">{k}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={
                      resetting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <RestartAltIcon />
                      )
                    }
                    onClick={openConfirm}
                    disabled={resetting}
                    data-testid="reset-open-confirm"
                  >
                    Reset account
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {result && (
            <Grid size={{ xs: 12, md: 8, lg: 6 }}>
              <Card sx={adminSurfaceSx(theme)}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Result
                  </Typography>
                  <List dense disablePadding>
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Target"
                        secondary={result.target_email}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary="Scope" secondary={result.scope} />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText
                        primary="Workouts deleted"
                        secondary={String(result.workouts_deleted ?? 0)}
                      />
                    </ListItem>
                  </List>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, display: "block", mt: 1.5, mb: 0.5 }}
                  >
                    Tables cleared ({result.tables_cleared?.length ?? 0})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(result.tables_cleared ?? []).map((t) => (
                      <Chip key={t} label={t} size="small" variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: 12,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              backgroundImage: "none",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            Confirm {SCOPES[scope].label.toLowerCase()} reset
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This resets <strong>{trimmedTarget}</strong> with scope{" "}
              <strong>{scope}</strong>. This cannot be undone.
            </Typography>
          </Alert>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
            {SCOPES[scope].wipes.map((w) => (
              <li key={w}>
                <Typography variant="body2">{w}</Typography>
              </li>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <strong>{trimmedTarget}</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={trimmedTarget}
            autoComplete="off"
            slotProps={{ htmlInput: { "data-testid": "reset-confirm-email" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeConfirm} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            disabled={!armed}
            data-testid="reset-confirm"
          >
            Reset account
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
